/**
 * AdminConsole — visitor-logs + settings dashboard.
 *
 * Redesigned 2026-07-10 to match the Intellix "ADMIN CMS · Visitor Logs"
 * reference: fixed left sidebar (icon + label nav), spacious main content,
 * soft pastel KPI cards with lucide icons, date-range pill selector,
 * cleaner trend chart, top-data grid, filterable log table.
 *
 * Backend API surface is unchanged:
 *   GET  /api/admin/overview        (headline + time_series + top_*)
 *   GET  /api/marketing/ip-logs     (paginated visitor logs + stats)
 *   GET  /api/admin/settings
 *   PUT  /api/admin/settings
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  LayoutDashboard, Globe, Bot, Users, MapPin, Eye, Settings as SettingsIcon,
  LogOut, RefreshCw, RotateCw, FileDown, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, TrendingUp, Filter, Search, Languages, Play, Pause,
  Inbox, Fingerprint, ShieldCheck, Trash2,
} from 'lucide-react';
import { BACKEND_URL } from '../../lib/api';
import LeadsTab from './LeadsTab';
import VisitorInsights from './VisitorInsights';
import { enrollAdminPasskey, isPasskeySupported } from '../../lib/webauthn';

const API = BACKEND_URL;
const TOKEN_KEY = 'admin_console_token';

// Restrained corporate palette — light neutrals + subtle accents for KPI cards.
const P = {
  bg: '#F7F8FB',            // page background — cool very light gray
  surface: '#FFFFFF',
  ink: '#0F172A',           // primary text
  slate: '#475569',
  muted: '#94A3B8',
  border: '#E5E7EB',
  borderSoft: '#EEF0F4',
  primary: '#0A2540',       // brand navy
  primarySoft: '#EEF2F7',
  accent: '#4F46E5',        // brand accent (indigo) for active nav / links
  accentSoft: '#EEF2FF',
  danger: '#DC2626',
  dangerSoft: '#FEF2F2',
  success: '#059669',
  successSoft: '#ECFDF5',
  warn: '#D97706',
  warnSoft: '#FFFBEB',
  info: '#0369A1',
  infoSoft: '#F0F9FF',
  pink: '#DB2777',
  pinkSoft: '#FDF2F8',
};

const NAV = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'iplogs',   label: 'Visitors',  icon: Eye },
  { key: 'leads',    label: 'Leads',     icon: Inbox },
  { key: 'translations', label: 'Translations', icon: Languages },
  { key: 'settings', label: 'Settings',  icon: SettingsIcon },
];

const getToken = () => localStorage.getItem(TOKEN_KEY);
const authHead = () => ({ Authorization: `Bearer ${getToken()}` });

// ─────────────────────────────────────────────────────────────────────
export default function AdminConsole() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [unauth, setUnauth] = useState(false);

  useEffect(() => { if (!getToken()) navigate('/loginmanage', { replace: true }); }, [navigate]);
  useEffect(() => {
    if (!unauth) return;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('admin_console_email');
    navigate('/loginmanage', { replace: true });
  }, [unauth, navigate]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('admin_console_email');
    navigate('/loginmanage', { replace: true });
  };

  const onUnauth = React.useCallback(() => setUnauth(true), []);

  if (unauth) return null;

  return (
    <div style={{
      minHeight: '100vh', background: P.bg, color: P.ink,
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
      display: 'grid', gridTemplateColumns: '240px 1fr',
    }} className="admin-shell">
      <Sidebar tab={tab} setTab={setTab} onLogout={logout} />
      <main style={{ padding: '32px clamp(20px, 3vw, 48px) 80px', maxWidth: 1400, width: '100%', boxSizing: 'border-box' }}>
        {tab === 'overview' && <OverviewTab onUnauth={() => setUnauth(true)} />}
        {tab === 'iplogs'   && <VisitorInsights onUnauth={onUnauth} />}
        {tab === 'leads'    && <LeadsTab onUnauth={() => setUnauth(true)} />}
        {tab === 'translations' && <TranslationsTab onUnauth={() => setUnauth(true)} />}
        {tab === 'settings' && <SettingsTab onUnauth={() => setUnauth(true)} />}
      </main>
      <style>{`
        @media (max-width: 900px) {
          .admin-shell { grid-template-columns: minmax(0, 1fr) !important; }
          .admin-shell > * { min-width: 0; max-width: 100vw; }
          .admin-sidebar { min-height: 0 !important; }
          .admin-sidebar { position: sticky; top: 0; z-index: 40; }
          .admin-sidebar-nav { display: flex !important; overflow-x: auto; padding: 8px !important; }
          .admin-sidebar-nav > * { flex: 0 0 auto; width: auto !important; margin-bottom: 0 !important; }
          .admin-sidebar-footer { display: none !important; }
          .admin-sidebar-brand { padding: 12px 16px !important; }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────── Sidebar ─────────────────────────────────
function Sidebar({ tab, setTab, onLogout }) {
  const email = localStorage.getItem('admin_console_email') || 'admin';
  return (
    <aside className="admin-sidebar" style={{
      background: P.surface, borderRight: `1px solid ${P.border}`,
      display: 'flex', flexDirection: 'column', minHeight: '100vh',
      position: 'sticky', top: 0, alignSelf: 'start',
    }}>
      <div className="admin-sidebar-brand" style={{ padding: '24px 20px', borderBottom: `1px solid ${P.borderSoft}` }}>
        <div style={{ fontSize: 10.5, letterSpacing: '0.24em', textTransform: 'uppercase', color: P.accent, fontWeight: 700 }}>Admin CMS</div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 20, fontWeight: 700, marginTop: 4, color: P.ink, letterSpacing: '-0.01em' }}>
          Keystone
        </div>
        <div style={{ fontSize: 11.5, color: P.muted, marginTop: 2 }}>by CloudVoro</div>
      </div>

      <nav className="admin-sidebar-nav" style={{ flex: 1, padding: '16px 12px' }}>
        {NAV.map((item) => {
          const active = tab === item.key;
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              data-testid={`admin-nav-${item.key}`}
              onClick={() => setTab(item.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                width: '100%', padding: '10px 12px', marginBottom: 4,
                background: active ? P.accentSoft : 'transparent',
                color: active ? P.accent : P.slate,
                border: 'none', borderRadius: 8, cursor: 'pointer',
                fontSize: 13.5, fontWeight: active ? 700 : 500,
                textAlign: 'left', position: 'relative', transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = P.borderSoft; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
            >
              {active && <span style={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, background: P.accent, borderRadius: 2 }} />}
              <Icon size={17} strokeWidth={active ? 2.2 : 1.8} />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="admin-sidebar-footer" style={{ padding: '16px 12px', borderTop: `1px solid ${P.borderSoft}` }}>
        <div style={{ padding: '10px 12px', fontSize: 12, color: P.muted, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          Signed in as
          <div style={{ color: P.ink, fontWeight: 600, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{email}</div>
        </div>
        <button
          data-testid="admin-logout"
          onClick={onLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            width: '100%', padding: '10px 12px', marginTop: 4,
            background: 'transparent', color: P.slate, border: 'none',
            borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, textAlign: 'left',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = P.dangerSoft; e.currentTarget.style.color = P.danger; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = P.slate; }}
        >
          <LogOut size={16} strokeWidth={1.8} />
          Sign out
        </button>
      </div>
    </aside>
  );
}

// ─────────────────────────── Overview Tab ────────────────────────────
function OverviewTab({ onUnauth }) {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [botFilter, setBotFilter] = useState('');
  const [err, setErr] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const [backfill, setBackfill] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null); setErr('');
    axios.get(`${API}/api/admin/overview?days=${days}&bot=${botFilter}`, { headers: authHead() })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (e?.response?.status === 401) onUnauth();
        else setErr(e?.message || 'Failed to load');
      });
    return () => { cancelled = true; };
  }, [days, botFilter, onUnauth, refreshTick]);

  // Poll backfill progress while running, then stop when done.
  useEffect(() => {
    if (!backfill?.running) return undefined;
    const id = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/marketing/ip-logs/backfill`, { headers: authHead() });
        setBackfill(r.data.state);
        if (!r.data.state?.running) {
          clearInterval(id);
          setRefreshTick((t) => t + 1); // refresh overview once done
        }
      } catch {
        clearInterval(id);
      }
    }, 2000);
    return () => clearInterval(id);
  }, [backfill?.running]);

  const startBackfill = async () => {
    try {
      await axios.post(`${API}/api/marketing/ip-logs/backfill?max_rows=5000`, null, { headers: authHead() });
      const r = await axios.get(`${API}/api/marketing/ip-logs/backfill`, { headers: authHead() });
      setBackfill(r.data.state);
    } catch (e) {
      setErr(e?.message || 'Backfill failed to start');
    }
  };

  if (err) return <PageHeader title="Overview"><EmptyState text={err} /></PageHeader>;

  return (
    <div data-testid="admin-overview">
      <PageHeader
        title="Visitor Logs"
        eyebrow="Insights"
        description="Who's hitting the sites, from where, how often. Auto-purge after 12 months."
        actions={
          <>
            <IconButton label="Refresh" icon={RefreshCw} onClick={() => setRefreshTick((t) => t + 1)} testId="btn-refresh" />
            <IconButton label="Export CSV" icon={FileDown} onClick={() => window.open(`${API}/api/marketing/ip-logs/export?token=${getToken()}`, '_blank')} testId="btn-export" />
            <IconButton label={backfill?.running ? 'Backfilling…' : 'Backfill geo'} icon={MapPin} onClick={startBackfill} testId="btn-backfill" />
            <IconButton label="Rotate logs" icon={RotateCw} tone="danger" testId="btn-rotate" />
          </>
        }
      />

      {backfill && (backfill.running || backfill.done > 0) && (
        <div data-testid="backfill-progress" style={{
          background: backfill.running ? P.accentSoft : P.borderSoft,
          border: `1px solid ${P.borderSoft}`,
          borderRadius: 8, padding: '10px 14px', marginBottom: 16,
          fontSize: 12.5, color: P.slate, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 700, color: P.ink }}>
            {backfill.running ? 'Geo backfill running' : 'Backfill finished'}
          </span>
          <span>{backfill.done}/{backfill.total} IPs processed</span>
          <span>· {backfill.enriched} enriched</span>
          <span>· {backfill.skipped_private} private / skipped</span>
          <span>· {backfill.errors} errors</span>
          {backfill.last_ip && <span style={{ fontFamily: 'ui-monospace, monospace', color: P.muted }}>last: {backfill.last_ip}</span>}
        </div>
      )}

      {/* Date range selector */}
      <FilterBar>
        <FilterLabel>Date range</FilterLabel>
        {[
          [1, 'Today'], [7, 'Last 7 days'], [30, 'Last 30 days'],
          [90, 'Last 90 days'], [365, 'All time'],
        ].map(([d, label]) => (
          <PillBtn key={d} active={d === days} onClick={() => setDays(d)} testId={`range-${d}`}>{label}</PillBtn>
        ))}
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <FilterLabel>Audience</FilterLabel>
          {[['', 'All'], ['false', 'Humans'], ['true', 'Bots']].map(([v, l]) => (
            <PillBtn key={v} active={v === botFilter} onClick={() => setBotFilter(v)} testId={`aud-${v || 'all'}`}>{l}</PillBtn>
          ))}
        </span>
      </FilterBar>

      {!data ? <LoadingState /> : (
        <>
          {/* KPI cards */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14, marginBottom: 28,
          }}>
            <KpiCard label="Visits" value={data.headline.total} icon={TrendingUp} tone="primary" testId="kpi-total" />
            <KpiCard label="Humans" value={data.headline.humans} icon={Users} tone="success" testId="kpi-humans" />
            <KpiCard label="Bots / Crawlers" value={data.headline.bots} icon={Bot} tone="warn" testId="kpi-bots" />
            <KpiCard label="Unique IPs" value={data.headline.unique_ips} icon={Globe} tone="info" testId="kpi-ips" />
            <KpiCard label="Countries" value={data.headline.unique_countries} icon={MapPin} tone="pink" testId="kpi-countries" />
          </div>

          {/* Trend chart */}
          <SectionCard
            title="Daily Trend"
            subtitle={`${data.time_series?.length || 0} days · max ${Math.max(1, ...(data.time_series || []).map((s) => s.total))}`}
          >
            <TrendChart series={data.time_series} />
          </SectionCard>

          {/* Top-data grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginTop: 20 }} className="ov-tri">
            <MiniTable title="Top Countries" rows={data.top_countries} />
            <MiniTable title="Top Browsers"  rows={data.top_browsers} />
            <MiniTable title="Top Devices"   rows={data.top_devices} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16, marginTop: 16 }} className="ov-duo">
            <MiniTable title="Top Pages" rows={data.top_pages} isPath />
            <MiniTable title="Top Referrers" rows={data.top_referrers} />
          </div>

          <div style={{ marginTop: 16 }}>
            <TopIpsTable rows={data.top_ips} />
          </div>

          <SectionCard title="Recent Visits" subtitle="Newest first">
            <RecentTable rows={data.recent} />
          </SectionCard>
        </>
      )}

      <style>{`
        @media (max-width: 1000px) {
          .ov-tri, .ov-duo { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────── Translations Tab ────────────────────────
function TranslationsTab({ onUnauth }) {
  const [jobs, setJobs] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const load = React.useCallback(() => {
    axios.get(`${API}/api/translations/jobs`, { headers: authHead() })
      .then((r) => { setJobs(r.data.jobs || []); setErr(''); })
      .catch((e) => { if (e?.response?.status === 401) onUnauth(); else setErr(e?.message || 'Failed to load'); });
  }, [onUnauth]);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const act = async (lang, action) => {
    setBusy(lang);
    try {
      await axios.post(`${API}/api/translations/jobs/${lang}/${action}`, {}, { headers: authHead() });
      load();
    } catch (e) {
      if (e?.response?.status === 401) onUnauth();
      else setErr(e?.response?.data?.detail || e?.message || 'Action failed');
    } finally { setBusy(''); }
  };

  if (err && !jobs) return <PageHeader title="Translations"><EmptyState text={err} /></PageHeader>;
  if (!jobs) return <><PageHeader title="Translations" /><LoadingState /></>;

  return (
    <div data-testid="admin-translations">
      <PageHeader
        title="Translations"
        eyebrow="Localisation"
        description="AI translation jobs for the marketing site. Start once per language — every phrase is translated once, cached in Mongo, then served forever."
        actions={(
          <button type="button" data-testid="translations-refresh" onClick={load} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
            background: P.surface, border: `1px solid ${P.border}`, borderRadius: 8,
            fontSize: 13, fontWeight: 600, color: P.slate, cursor: 'pointer',
          }}><RefreshCw size={14} /> Refresh</button>
        )}
      />

      <SectionCard title="Marketing site translation jobs" subtitle="Pause anytime — resuming skips everything already translated.">
        <div style={{ display: 'grid', gap: 14 }}>
          {jobs.map((j) => {
            const pct = j.corpus_total ? Math.min(100, Math.round((j.cached / j.corpus_total) * 100)) : 0;
            const running = j.status === 'running' && j.worker_alive;
            const statusColor = running ? P.success : j.status === 'paused' ? P.warn : P.muted;
            return (
              <div key={j.lang} data-testid={`admin-job-${j.lang}`} style={{
                border: `1px solid ${P.borderSoft}`, borderRadius: 10, padding: '16px 18px', background: P.surface,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: P.ink }}>{j.language}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: P.muted }}>{j.lang}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span data-testid={`admin-job-status-${j.lang}`} style={{
                      fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: statusColor,
                    }}>{running ? 'running' : j.status}</span>
                    {running ? (
                      <button type="button" data-testid={`admin-pause-${j.lang}`} disabled={busy === j.lang}
                        onClick={() => act(j.lang, 'pause')} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                          background: P.surface, border: `1px solid ${P.border}`, borderRadius: 8,
                          fontSize: 12.5, fontWeight: 600, color: P.slate, cursor: 'pointer',
                        }}><Pause size={13} /> Pause</button>
                    ) : (
                      <button type="button" data-testid={`admin-start-${j.lang}`} disabled={busy === j.lang || pct >= 100}
                        onClick={() => act(j.lang, 'start')} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                          background: pct >= 100 ? P.successSoft : P.primary,
                          border: 'none', borderRadius: 8,
                          fontSize: 12.5, fontWeight: 700, color: pct >= 100 ? P.success : '#fff',
                          cursor: pct >= 100 ? 'default' : 'pointer',
                        }}><Play size={13} /> {pct >= 100 ? 'Done' : pct > 0 ? 'Resume' : 'Start'}</button>
                    )}
                  </div>
                </div>
                <div style={{ height: 8, borderRadius: 99, background: P.borderSoft, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: running ? P.success : P.accent, transition: 'width .4s ease' }} />
                </div>
                <div data-testid={`admin-job-progress-${j.lang}`} style={{ fontSize: 12, color: P.muted, marginTop: 7 }}>
                  {j.cached} / {j.corpus_total} strings translated ({pct}%)
                  {j.failed ? ` · ${j.failed} failed (retried on next run)` : ''}
                </div>
              </div>
            );
          })}
        </div>
        {err && <div style={{ marginTop: 12, fontSize: 12.5, color: P.danger }}>{err}</div>}
        <p style={{ fontSize: 12, color: P.muted, marginTop: 14, marginBottom: 0 }}>
          Translations serve instantly from the browser cache — no rebuild or redeploy needed. Complete a language once, every visitor picks it up on their next page load (fresh visitors download the bundle in one call, then hit a 304 forever after).
        </p>
      </SectionCard>

      <TranslationTools onDone={load} />
    </div>
  );
}


function TranslationTools({ onDone }) {
  const [words, setWords] = useState('');
  const [langs, setLangs] = useState({ hu: true, pl: true, de: true });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ text: '', tone: '' });
  const [wipeLang, setWipeLang] = useState('');
  const [wipeConfirm, setWipeConfirm] = useState('');
  const [observed, setObserved] = useState({ items: [], pending_total: 0 });

  const loadObserved = React.useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/translations/observed?limit=200`, { headers: authHead() });
      setObserved(data);
    } catch { /* silent — this panel is optional */ }
  }, []);

  useEffect(() => { loadObserved(); }, [loadObserved]);

  const promoteAll = async () => {
    if (!observed.pending_total) return;
    if (!window.confirm(`Translate and promote ${observed.pending_total} runtime-observed strings across HU/PL/DE? This will use LLM tokens.`)) return;
    setBusy(true); setMsg({ text: '', tone: '' });
    try {
      const { data } = await axios.post(`${API}/api/translations/promote-observed`,
        { min_count: 1 }, { headers: authHead() });
      const summary = Object.entries(data.per_lang || {}).map(([l, v]) => `${l}: ${v.translated}${v.failed ? ` (${v.failed} failed)` : ''}`).join(' · ');
      setMsg({ text: `Promoted ${data.promoted} observed strings — ${summary}`, tone: 'success' });
      loadObserved();
      onDone?.();
    } catch (e) {
      setMsg({ text: e?.response?.data?.detail || e?.message || 'Promote failed', tone: 'danger' });
    } finally { setBusy(false); }
  };

  const fix = async () => {
    setBusy(true); setMsg({ text: '', tone: '' });
    try {
      const strings = words.split('\n').map(s => s.trim()).filter(Boolean);
      if (!strings.length) { setMsg({ text: 'Enter at least one word to retranslate.', tone: 'danger' }); return; }
      const selected = Object.entries(langs).filter(([, on]) => on).map(([l]) => l);
      const { data } = await axios.post(`${API}/api/translations/retranslate`,
        { strings, langs: selected }, { headers: authHead() });
      const summary = Object.entries(data.results || {}).map(([l, r]) =>
        r.ok ? `${l}: ${r.count}` : `${l}: FAILED (${r.error?.slice(0, 60)})`).join(' · ');
      setMsg({ text: `Retranslated ${strings.length} string(s) — ${summary}`, tone: 'success' });
      setWords('');
      onDone?.();
    } catch (e) {
      setMsg({ text: e?.response?.data?.detail || e?.message || 'Failed', tone: 'danger' });
    } finally { setBusy(false); }
  };

  const wipe = async () => {
    if (!wipeLang) { setMsg({ text: 'Pick a language to wipe.', tone: 'danger' }); return; }
    setBusy(true); setMsg({ text: '', tone: '' });
    try {
      const { data } = await axios.post(`${API}/api/translations/jobs/${wipeLang}/wipe-and-restart`,
        { confirm: wipeConfirm }, { headers: authHead() });
      setMsg({ text: `Wiped ${data.cleared} strings for ${wipeLang.toUpperCase()} and restarted job.`, tone: 'success' });
      setWipeLang(''); setWipeConfirm('');
      onDone?.();
    } catch (e) {
      setMsg({ text: e?.response?.data?.detail || e?.message || 'Failed', tone: 'danger' });
    } finally { setBusy(false); }
  };

  return (
    <>
      <SectionCard
        title="Fix a translation"
        subtitle="Spot a word being translated wrong (e.g. 'Dashboard' → car dashboard)? Paste one per line and re-translate with the current context-aware prompt. Only the words you list are affected — every client picks up the fix on their next page load.">
        <div style={{ display: 'grid', gap: 12 }} data-testid="translation-fix-tool">
          <textarea
            data-testid="fix-words-input"
            rows={4} value={words} onChange={(e) => setWords(e.target.value)}
            placeholder="Dashboard&#10;Sandbox&#10;Batch"
            style={{
              padding: '12px 14px', border: `1px solid ${P.border}`, borderRadius: 8,
              background: P.surface, fontSize: 14, color: P.ink,
              fontFamily: 'ui-monospace, SFMono-Regular, monospace', outline: 'none', resize: 'vertical',
            }}/>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: P.muted, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Apply to:</span>
            {Object.entries(langs).map(([l, on]) => (
              <label key={l} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', color: P.ink }}>
                <input type="checkbox" checked={on}
                       onChange={(e) => setLangs({ ...langs, [l]: e.target.checked })}
                       style={{ accentColor: P.accent }} />
                {l.toUpperCase()}
              </label>
            ))}
            <button data-testid="fix-submit" onClick={fix} disabled={busy}
              style={{
                marginLeft: 'auto', padding: '10px 18px', background: P.primary, color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
                cursor: busy ? 'wait' : 'pointer',
              }}>
              {busy ? 'Working…' : 'Retranslate these words'}
            </button>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title={`Runtime-observed strings${observed.pending_total ? ` (${observed.pending_total} pending)` : ''}`}
        subtitle="Whenever a visitor loads a marketing page in HU/PL/DE, the DomTranslator reports any strings that aren't in the persistent cache. Strings that live outside the corpus (button labels, dynamic copy, conditional text) show up here after the first visitor sees them. Click 'Promote & translate' to close the gap in one shot.">
        <div data-testid="observed-strings-card">
          {observed.pending_total === 0 ? (
            <div style={{ padding: '18px 20px', background: P.successSoft, color: P.success,
                          borderRadius: 8, fontSize: 13.5, border: `1px solid ${P.success}22` }}>
              No pending observed strings. Visit the marketing pages in HU/PL/DE (via the language switcher) and any missing strings will appear here automatically.
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, color: P.ink }}>
                  <b>{observed.pending_total}</b> string{observed.pending_total !== 1 ? 's' : ''} observed by real visitors, not yet in the cache.
                </span>
                <button data-testid="promote-observed-btn" onClick={promoteAll} disabled={busy}
                  style={{
                    marginLeft: 'auto', padding: '10px 16px', background: P.primary, color: '#fff',
                    border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
                    cursor: busy ? 'wait' : 'pointer',
                  }}>
                  {busy ? 'Translating…' : `Promote & translate all ${observed.pending_total}`}
                </button>
              </div>
              <div style={{ maxHeight: 220, overflowY: 'auto', border: `1px solid ${P.border}`, borderRadius: 8 }}>
                {(observed.items || []).slice(0, 40).map((it, i) => (
                  <div key={i} style={{
                    padding: '8px 12px', borderTop: i === 0 ? 'none' : `1px solid ${P.borderSoft}`,
                    display: 'flex', gap: 12, alignItems: 'center', fontSize: 12.5,
                  }}>
                    <span style={{
                      minWidth: 32, textAlign: 'right', color: P.muted,
                      fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                    }}>×{it.count}</span>
                    <span style={{ color: P.ink, wordBreak: 'break-word', flex: 1 }}>{it.src}</span>
                  </div>
                ))}
                {observed.items.length > 40 && (
                  <div style={{ padding: '8px 12px', borderTop: `1px solid ${P.borderSoft}`, color: P.muted, fontSize: 12 }}>
                    …and {observed.pending_total - 40} more. All get translated on Promote.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title="Wipe & retranslate everything (danger zone)"
        subtitle="Use this after prompt-level improvements. Deletes ALL cached translations for the chosen language and re-runs the job from scratch. Burns LLM tokens — expect ~5-10 minutes per language.">
        <div style={{
          padding: 16, border: `1px solid ${P.danger}33`, background: P.dangerSoft,
          borderRadius: 8, display: 'grid', gap: 12,
        }} data-testid="translation-wipe-tool">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <select data-testid="wipe-lang-select" value={wipeLang}
                    onChange={(e) => setWipeLang(e.target.value)}
                    style={{
                      padding: '10px 12px', border: `1px solid ${P.border}`, borderRadius: 8,
                      background: P.surface, fontSize: 13.5, color: P.ink, outline: 'none',
                    }}>
              <option value="">Pick a language…</option>
              <option value="hu">Hungarian (HU)</option>
              <option value="pl">Polish (PL)</option>
              <option value="de">German (DE)</option>
            </select>
            <input data-testid="wipe-confirm-input" value={wipeConfirm}
                   onChange={(e) => setWipeConfirm(e.target.value)}
                   placeholder='Type "WIPE AND RETRANSLATE" to confirm'
                   style={{
                     flex: 1, minWidth: 260, padding: '10px 12px',
                     border: `1px solid ${P.border}`, borderRadius: 8, background: P.surface,
                     fontSize: 13, color: P.ink, outline: 'none',
                     fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                   }}/>
            <button data-testid="wipe-submit" onClick={wipe}
                    disabled={busy || wipeConfirm.trim().toUpperCase() !== 'WIPE AND RETRANSLATE' || !wipeLang}
                    style={{
                      padding: '10px 16px', background: P.danger, color: '#fff',
                      border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
                      cursor: (busy || wipeConfirm.trim().toUpperCase() !== 'WIPE AND RETRANSLATE' || !wipeLang) ? 'not-allowed' : 'pointer',
                      opacity: (wipeConfirm.trim().toUpperCase() !== 'WIPE AND RETRANSLATE' || !wipeLang) ? 0.5 : 1,
                    }}>
              Wipe & restart
            </button>
          </div>
        </div>
      </SectionCard>

      {msg.text && (
        <div data-testid="translation-tool-msg" style={{
          marginTop: 14, padding: '12px 16px', borderRadius: 8, fontSize: 13.5,
          background: msg.tone === 'success' ? P.successSoft : P.dangerSoft,
          color: msg.tone === 'success' ? P.success : P.danger,
          border: `1px solid ${(msg.tone === 'success' ? P.success : P.danger)}22`,
        }}>{msg.text}</div>
      )}
    </>
  );
}


// ─────────────────────────── Settings Tab ────────────────────────────
function FoundersClubCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = () => {
    axios.get(`${API}/api/marketing/founders-club`)
      .then((r) => setState(r.data))
      .catch(() => setErr('Could not load Founder\u2019s Club status'));
  };
  useEffect(load, []);

  const toggle = async () => {
    if (!state || busy) return;
    const next = !state.closed;
    const label = next
      ? 'Close the Founder\u2019s Club? The offer strip disappears from the site and the waitlist form appears on /founders-club.'
      : 'Reopen the Founder\u2019s Club? The offer becomes available on the site again.';
    if (!window.confirm(label)) return;
    setBusy(true); setErr('');
    try {
      await axios.put(`${API}/api/marketing/founders-club`, { closed: next }, { headers: authHead() });
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Update failed');
    } finally { setBusy(false); }
  };

  return (
    <SectionCard title="Founder's Club" subtitle="One switch: open shows the offer site-wide; closed hides the strip and turns on the waitlist on /founders-club. Seat numbers are never shown publicly.">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }} data-testid="founders-club-admin-card">
        <span data-testid="founders-club-admin-status" style={{
          padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 800,
          letterSpacing: '0.12em', textTransform: 'uppercase',
          background: state ? (state.closed ? '#FEF3C7' : '#DCFCE7') : '#F1F5F9',
          color: state ? (state.closed ? '#92400E' : '#166534') : '#64748B',
        }}>
          {state ? (state.closed ? 'Closed' : 'Open') : 'Loading\u2026'}
        </span>
        <button onClick={toggle} disabled={!state || busy} data-testid="founders-club-admin-toggle" style={{
          padding: '10px 18px', borderRadius: 6, border: 'none', cursor: busy ? 'wait' : 'pointer',
          background: state && state.closed ? '#166534' : '#9F1239', color: '#fff',
          fontSize: 13, fontWeight: 700, opacity: !state || busy ? 0.6 : 1,
        }}>
          {busy ? 'Saving\u2026' : state && state.closed ? 'Reopen the club' : 'Close the club'}
        </button>
        {err && <span style={{ fontSize: 12.5, color: '#B91C1C' }}>{err}</span>}
      </div>
    </SectionCard>
  );
}

function SettingsTab({ onUnauth }) {
  const [s, setS] = useState(null);
  const [smtp, setSmtp] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState('');
  const [testState, setTestState] = useState({ sending: false, msg: '', tone: '' });
  const [testTo, setTestTo] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/admin/settings`, { headers: authHead() })
      .then((r) => { setS(r.data); setSmtp(smtpFromSettings(r.data)); })
      .catch((e) => { if (e?.response?.status === 401) onUnauth(); else setErr(e?.message || ''); });
  }, [onUnauth]);

  const save = async () => {
    setSaving(true); setErr('');
    try {
      // Password is only sent when the admin has entered a new value.
      // Empty string means "leave the stored password alone".
      const smtpPatch = { ...smtp };
      if (!smtpPatch._pw_touched) delete smtpPatch.marketing_smtp_password;
      delete smtpPatch._pw_touched;

      const { data } = await axios.put(`${API}/api/admin/settings`, {
        ga4_measurement_id: s.ga4_measurement_id || '',
        gsc_verification: s.gsc_verification || '',
        bing_verification: s.bing_verification || '',
        linkedin_partner_id: s.linkedin_partner_id || '',
        track_ip_logs: !!s.track_ip_logs,
        ...smtpPatch,
      }, { headers: authHead() });
      setS(data);
      setSmtp(smtpFromSettings(data));
      setSavedAt(Date.now());
    } catch (e) {
      if (e?.response?.status === 401) onUnauth();
      else setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (err) return <PageHeader title="Settings"><EmptyState text={err} /></PageHeader>;
  if (!s) return <><PageHeader title="Settings" /><LoadingState /></>;

  const set = (k) => (v) => setS({ ...s, [k]: v });

  return (
    <div data-testid="admin-settings">
      <FoundersClubCard />
      <PageHeader
        title="Settings"
        eyebrow="Configuration"
        description="Tracking IDs, verification tokens and internal logging."
      />

      <SectionCard title="Analytics & Verification" subtitle="Injected into every public page when set.">
        <SField label="Google Analytics 4 — Measurement ID" hint="Format: G-XXXXXXXXXX. From analytics.google.com → Admin → Data Streams.">
          <TextInput data-testid="set-ga4" value={s.ga4_measurement_id || ''} onChange={(e) => set('ga4_measurement_id')(e.target.value)} placeholder="G-XXXXXXXXXX" />
        </SField>
        <SField label="Google Search Console — Verification" hint="Paste only the content= value from Search Console.">
          <TextInput data-testid="set-gsc" value={s.gsc_verification || ''} onChange={(e) => set('gsc_verification')(e.target.value)} placeholder="abc123…" />
        </SField>
        <SField label="Bing Webmaster Tools — msvalidate.01">
          <TextInput data-testid="set-bing" value={s.bing_verification || ''} onChange={(e) => set('bing_verification')(e.target.value)} placeholder="abc123…" />
        </SField>
        <SField label="LinkedIn Insight Tag — Partner ID (optional)" hint="Numeric ID from LinkedIn Campaign Manager.">
          <TextInput data-testid="set-li" value={s.linkedin_partner_id || ''} onChange={(e) => set('linkedin_partner_id')(e.target.value)} placeholder="123456" />
        </SField>
      </SectionCard>

      <SectionCard title="Contact form email (SMTP)" subtitle="Enquiries from the marketing site are delivered to your inbox via this SMTP account. If disabled, the server falls back to environment variables.">
        <label style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, color: P.ink, cursor: 'pointer', marginBottom: 14 }}>
          <input data-testid="set-smtp-enabled" type="checkbox"
                 checked={!!smtp.marketing_smtp_enabled}
                 onChange={(e) => setSmtp({ ...smtp, marketing_smtp_enabled: e.target.checked })}
                 style={{ width: 16, height: 16, accentColor: P.accent }} />
          Use these SMTP settings for contact-form enquiries
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0 20px' }}>
          <SField label="SMTP host" hint="e.g. mail.cloudvoro.com or smtp.gmail.com">
            <TextInput data-testid="set-smtp-host" value={smtp.marketing_smtp_host || ''}
              onChange={(e) => setSmtp({ ...smtp, marketing_smtp_host: e.target.value })} placeholder="mail.example.com" />
          </SField>
          <SField label="Port" hint="587 = TLS · 465 = SSL · 25 = none">
            <TextInput data-testid="set-smtp-port" type="number" min="1" max="65535"
              value={smtp.marketing_smtp_port || ''}
              onChange={(e) => setSmtp({ ...smtp, marketing_smtp_port: e.target.value ? Number(e.target.value) : '' })}
              placeholder="587" />
          </SField>
          <SField label="Encryption">
            <select data-testid="set-smtp-encryption" value={smtp.marketing_smtp_encryption || 'tls'}
              onChange={(e) => setSmtp({ ...smtp, marketing_smtp_encryption: e.target.value })}
              style={{
                width: '100%', padding: '10px 14px', border: `1px solid ${P.border}`, borderRadius: 8,
                background: P.surface, fontSize: 14, color: P.ink, fontFamily: 'inherit',
                boxSizing: 'border-box', outline: 'none',
              }}>
              <option value="tls">STARTTLS (port 587)</option>
              <option value="ssl">SSL/TLS (port 465)</option>
              <option value="none">None (plain — not recommended)</option>
            </select>
          </SField>
          <SField label="Username" hint="Usually the same as the From address">
            <TextInput data-testid="set-smtp-username" value={smtp.marketing_smtp_username || ''}
              onChange={(e) => setSmtp({ ...smtp, marketing_smtp_username: e.target.value })}
              placeholder="hello@cloudvoro.com" autoComplete="off" />
          </SField>
          <SField label="Password"
                  hint={smtp.marketing_smtp_password_set ? 'A password is saved. Leave blank to keep it — type a new value to change.' : 'Not set yet.'}>
            <TextInput data-testid="set-smtp-password" type="password"
              value={smtp.marketing_smtp_password || ''}
              onChange={(e) => setSmtp({ ...smtp, marketing_smtp_password: e.target.value, _pw_touched: true })}
              placeholder={smtp.marketing_smtp_password_set ? '••••••••••' : ''}
              autoComplete="new-password" />
          </SField>
          <SField label="From email" hint="What the recipient sees in the From: header">
            <TextInput data-testid="set-smtp-from-email" value={smtp.marketing_smtp_from_email || ''}
              onChange={(e) => setSmtp({ ...smtp, marketing_smtp_from_email: e.target.value })}
              placeholder="hello@cloudvoro.com" />
          </SField>
          <SField label="From name">
            <TextInput data-testid="set-smtp-from-name" value={smtp.marketing_smtp_from_name || ''}
              onChange={(e) => setSmtp({ ...smtp, marketing_smtp_from_name: e.target.value })}
              placeholder="Keystone by CloudVoro" />
          </SField>
          <SField label="Deliver enquiries to" hint="Every contact-form submission is emailed here.">
            <TextInput data-testid="set-smtp-inquiry-to" value={smtp.marketing_smtp_inquiry_to_email || ''}
              onChange={(e) => setSmtp({ ...smtp, marketing_smtp_inquiry_to_email: e.target.value })}
              placeholder="founder@cloudvoro.com" />
          </SField>
        </div>
        <div style={{
          marginTop: 6, padding: 14, background: P.borderSoft, borderRadius: 8,
          display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <input data-testid="test-smtp-to" value={testTo}
                 onChange={(e) => setTestTo(e.target.value)}
                 placeholder="Send test to… (leave blank to use 'Deliver enquiries to')"
                 style={{
                   flex: 1, minWidth: 220, padding: '9px 12px', border: `1px solid ${P.border}`,
                   borderRadius: 8, background: P.surface, fontSize: 13, color: P.ink, outline: 'none',
                 }} />
          <button
            data-testid="test-smtp-btn"
            onClick={async () => {
              setTestState({ sending: true, msg: '', tone: '' });
              try {
                const { data } = await axios.post(`${API}/api/admin/settings/test-smtp`,
                  testTo ? { to: testTo } : {}, { headers: authHead() });
                setTestState({ sending: false, msg: `Sent to ${data.sent_to} via ${data.host}:${data.port}`, tone: 'success' });
              } catch (e) {
                setTestState({ sending: false,
                                msg: e?.response?.data?.detail || e?.message || 'Test failed',
                                tone: 'danger' });
              }
            }}
            disabled={testState.sending}
            style={{
              padding: '9px 14px', background: P.primary, color: '#fff',
              border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: testState.sending ? 'wait' : 'pointer',
            }}>
            {testState.sending ? 'Sending…' : 'Send test email'}
          </button>
          {testState.msg && (
            <span data-testid="test-smtp-msg" style={{
              fontSize: 12.5, fontWeight: 600,
              color: testState.tone === 'success' ? P.success : P.danger,
            }}>{testState.msg}</span>
          )}
        </div>
        <p style={{ fontSize: 12, color: P.muted, marginTop: 10, lineHeight: 1.5 }}>
          Save your changes first, then click <b>Send test email</b>. If the test succeeds, real enquiries will use the same settings.
        </p>
      </SectionCard>

      <SectionCard title="Internal Tracking" subtitle="First-party visitor logs stored in your Mongo. Never shared.">
        <label style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 14, color: P.ink, cursor: 'pointer' }}>
          <input data-testid="set-track" type="checkbox" checked={!!s.track_ip_logs} onChange={(e) => set('track_ip_logs')(e.target.checked)} style={{ width: 16, height: 16, accentColor: P.accent }} />
          Record every public-page visit (IP, path, user-agent, geo)
        </label>
      </SectionCard>

      <AdminPasskeys onUnauth={onUnauth} />

      <div style={{ marginTop: 24, display: 'flex', gap: 14, alignItems: 'center' }}>
        <button
          data-testid="settings-save"
          onClick={save}
          disabled={saving}
          style={{
            padding: '12px 24px', background: saving ? P.slate : P.primary,
            color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13.5,
            letterSpacing: '0.02em', cursor: saving ? 'wait' : 'pointer',
          }}
        >{saving ? 'Saving…' : 'Save changes'}</button>
        {savedAt && <span style={{ fontSize: 12, color: P.success, fontWeight: 600 }} data-testid="settings-saved">✓ Saved at {new Date(savedAt).toLocaleTimeString()}</span>}
      </div>
    </div>
  );
}

// ─────────────────────────── Building Blocks ─────────────────────────
function PageHeader({ title, eyebrow, description, actions, children }) {
  return (
    <>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        gap: 24, marginBottom: 24, flexWrap: 'wrap',
      }}>
        <div>
          {eyebrow && <div style={{ fontSize: 10.5, letterSpacing: '0.22em', textTransform: 'uppercase', color: P.accent, fontWeight: 700, marginBottom: 8 }}>{eyebrow}</div>}
          <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 34, fontWeight: 700, color: P.ink, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.1 }}>{title}</h1>
          {description && <p style={{ fontSize: 14.5, color: P.slate, marginTop: 8, maxWidth: 680, lineHeight: 1.55 }}>{description}</p>}
        </div>
        {actions && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{actions}</div>}
      </div>
      {children}
    </>
  );
}

function IconButton({ label, icon: Icon, onClick, tone, testId }) {
  const danger = tone === 'danger';
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '9px 14px', border: `1px solid ${danger ? '#FCA5A5' : P.border}`,
        background: danger ? P.dangerSoft : P.surface,
        color: danger ? P.danger : P.slate,
        borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
        transition: 'all 0.15s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = danger ? '#FEE2E2' : P.borderSoft; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = danger ? P.dangerSoft : P.surface; }}
    >
      <Icon size={14} strokeWidth={2} />
      {label}
    </button>
  );
}

function FilterBar({ children }) {
  return (
    <div style={{
      background: P.surface, border: `1px solid ${P.border}`, borderRadius: 12,
      padding: '14px 18px', marginBottom: 24,
      display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
    }}>{children}</div>
  );
}

function FilterLabel({ children }) {
  return <span style={{ fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: P.muted, fontWeight: 700, marginRight: 6 }}>{children}</span>;
}

function PillBtn({ active, onClick, testId, children }) {
  return (
    <button
      data-testid={testId} onClick={onClick}
      style={{
        padding: '7px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: active ? 700 : 500,
        background: active ? P.primary : 'transparent',
        color: active ? '#fff' : P.slate,
        border: `1px solid ${active ? P.primary : P.border}`,
        cursor: 'pointer', transition: 'all 0.15s',
      }}
    >{children}</button>
  );
}

function KpiCard({ label, value, icon: Icon, tone, testId }) {
  const toneMap = {
    primary: { bg: P.primarySoft, fg: P.primary },
    success: { bg: P.successSoft, fg: P.success },
    warn:    { bg: P.warnSoft,    fg: P.warn },
    info:    { bg: P.infoSoft,    fg: P.info },
    pink:    { bg: P.pinkSoft,    fg: P.pink },
  };
  const t = toneMap[tone] || toneMap.primary;
  return (
    <div data-testid={testId} style={{
      background: P.surface, borderRadius: 12, border: `1px solid ${P.border}`,
      padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 14,
      minHeight: 128, position: 'relative', overflow: 'hidden',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: t.bg, color: t.fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon size={20} strokeWidth={2} />
      </div>
      <div>
        <div style={{ fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase', color: P.muted, fontWeight: 700, marginBottom: 6 }}>{label}</div>
        <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 32, fontWeight: 700, color: P.ink, letterSpacing: '-0.02em', lineHeight: 1 }}>
          {(value || 0).toLocaleString()}
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, subtitle, children, flush }) {
  return (
    <section style={{
      background: P.surface, borderRadius: 12, border: `1px solid ${P.border}`,
      marginTop: 20, overflow: 'hidden',
    }}>
      {(title || subtitle) && (
        <div style={{ padding: '18px 22px', borderBottom: flush ? 'none' : `1px solid ${P.borderSoft}` }}>
          {title && <div style={{ fontSize: 15, fontWeight: 700, color: P.ink, letterSpacing: '-0.005em' }}>{title}</div>}
          {subtitle && <div style={{ fontSize: 12.5, color: P.muted, marginTop: 3 }}>{subtitle}</div>}
        </div>
      )}
      <div style={{ padding: flush ? '18px 22px' : '20px 22px' }}>{children}</div>
    </section>
  );
}

function TrendChart({ series }) {
  if (!series || !series.length) return <div style={{ color: P.muted, fontSize: 13, padding: '20px 0' }}>No data in this window yet.</div>;
  const W = 900, H = 240, pad = { top: 16, right: 16, bottom: 32, left: 40 };
  const max = Math.max(1, ...series.map((s) => s.total));
  const xStep = (W - pad.left - pad.right) / Math.max(1, series.length - 1);
  const yFor = (v) => H - pad.bottom - ((v / max) * (H - pad.top - pad.bottom));
  const total = series.map((s, i) => [pad.left + i * xStep, yFor(s.total)]);
  const humans = series.map((s, i) => [pad.left + i * xStep, yFor(s.humans)]);
  const pathStr = (pts) => pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const areaStr = `${pathStr(total)} L${pad.left + (series.length - 1) * xStep},${H - pad.bottom} L${pad.left},${H - pad.bottom} Z`;

  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', minWidth: 400, display: 'block' }}>
          <defs>
            <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={P.accent} stopOpacity="0.20" />
              <stop offset="100%" stopColor={P.accent} stopOpacity="0" />
            </linearGradient>
          </defs>
          {[0, 0.25, 0.5, 0.75, 1].map((p) => (
            <g key={p}>
              <line x1={pad.left} x2={W - pad.right} y1={yFor(max * p)} y2={yFor(max * p)} stroke={P.borderSoft} strokeWidth="1" />
              <text x={pad.left - 8} y={yFor(max * p) + 3} fontSize="10" fill={P.muted} textAnchor="end">{Math.round(max * p)}</text>
            </g>
          ))}
          <path d={areaStr} fill="url(#trendGrad)" />
          <path d={pathStr(total)} fill="none" stroke={P.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          <path d={pathStr(humans)} fill="none" stroke={P.success} strokeWidth="1.8" strokeDasharray="5 4" strokeLinecap="round" />
          {series.length <= 60 && total.map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r="3" fill={P.accent} />
          ))}
          {series.length > 0 && [0, Math.floor(series.length / 2), series.length - 1]
            .filter((v, i, a) => a.indexOf(v) === i)
            .map((idx) => (
              <text key={idx} x={pad.left + idx * xStep} y={H - 10} fontSize="10" fill={P.muted} textAnchor="middle">
                {series[idx]?.date?.slice(5) || ''}
              </text>
            ))}
        </svg>
      </div>
      <div style={{ display: 'flex', gap: 20, fontSize: 12, color: P.slate, marginTop: 8 }}>
        <LegendDot color={P.accent} label="Total" />
        <LegendDot color={P.success} label="Humans" dashed />
      </div>
    </div>
  );
}

function LegendDot({ color, label, dashed }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        display: 'inline-block', width: 18, height: 2.5,
        background: dashed ? undefined : color,
        borderTop: dashed ? `2px dashed ${color}` : 'none',
      }} />
      {label}
    </span>
  );
}

function TopIpsTable({ rows }) {
  if (!rows || !rows.length) {
    return (
      <SectionCard title="Top IPs (humans only)" flush>
        <div style={{ color: P.muted, fontSize: 13 }}>Nothing yet.</div>
      </SectionCard>
    );
  }
  return (
    <SectionCard title="Top IPs (humans only)" subtitle={`${rows.length} unique repeat visitors, bot IPs excluded`}>
      <div style={{ overflowX: 'auto', margin: '-4px -22px -20px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: P.borderSoft, color: P.slate }}>
              {['IP', 'Country', 'City', 'ISP', 'Last seen', 'Hits'].map((h) => <th key={h} style={tableTh}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${P.borderSoft}` }} data-testid={`top-ip-row-${i}`}>
                <td style={{ ...tableTd, fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{r.label || '—'}</td>
                <td style={tableTd}>{r.country_code ? `${r.country_code} ${r.country || ''}` : (r.country || '—')}</td>
                <td style={tableTd}>{r.city || '—'}</td>
                <td style={{ ...tableTd, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.isp}>{r.isp || '—'}</td>
                <td style={{ ...tableTd, color: P.slate, fontVariantNumeric: 'tabular-nums' }}>{r.last_seen?.slice(0, 16).replace('T', ' ') || '—'}</td>
                <td style={{ ...tableTd, fontWeight: 700, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
}

function MiniTable({ title, rows, isPath }) {
  const max = Math.max(1, ...(rows || []).map((r) => r.count));
  return (
    <SectionCard title={title} flush>
      {(!rows || !rows.length) ? (
        <div style={{ color: P.muted, fontSize: 13 }}>Nothing yet.</div>
      ) : (
        <div>
          {rows.slice(0, 8).map((r, i) => {
            const pct = (r.count / max) * 100;
            return (
              <div key={i} style={{ position: 'relative', margin: '4px 0', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, background: P.accentSoft, width: `${pct}%`, opacity: 0.55, transition: 'width 0.3s' }} />
                <div style={{
                  position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '7px 10px', fontSize: 12.5,
                }}>
                  <span style={{
                    color: P.ink, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    maxWidth: 220,
                    fontFamily: isPath ? 'ui-monospace, SFMono-Regular, monospace' : 'inherit',
                    fontSize: isPath ? 11.5 : 12.5,
                  }} title={r.label}>{r.label || '(empty)'}</span>
                  <span style={{ color: P.slate, fontWeight: 700, fontVariantNumeric: 'tabular-nums', marginLeft: 8 }}>{r.count}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

function RecentTable({ rows }) {
  if (!rows || !rows.length) return <div style={{ color: P.muted, fontSize: 13 }}>No recent visits.</div>;
  return (
    <div style={{ overflowX: 'auto', margin: '-4px -22px -20px' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
        <thead>
          <tr style={{ background: P.borderSoft, color: P.slate }}>
            {['Time', 'IP', 'Country', 'Path', 'Browser', 'Kind'].map((h) => <th key={h} style={tableTh}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: `1px solid ${P.borderSoft}` }}>
              <td style={tableTd}>{r.received_at?.slice(0, 16).replace('T', ' ') || '—'}</td>
              <td style={{ ...tableTd, fontFamily: 'ui-monospace, monospace' }}>{r.ip || '—'}</td>
              <td style={tableTd}>{r.country || '—'}</td>
              <td style={{ ...tableTd, fontFamily: 'ui-monospace, monospace', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.path || '—'}</td>
              <td style={tableTd}>{r.browser || '—'}</td>
              <td style={tableTd}>
                {r.is_bot ? <Badge tone="warn">bot</Badge> : <Badge tone="success">human</Badge>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FilterInput({ icon: Icon, placeholder, value, onChange, testId }) {
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      {Icon && <Icon size={14} style={{ position: 'absolute', left: 12, color: P.muted }} strokeWidth={2} />}
      <input
        data-testid={testId}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%', padding: Icon ? '9px 12px 9px 34px' : '9px 12px',
          border: `1px solid ${P.border}`, borderRadius: 8, background: P.surface,
          fontSize: 13, color: P.ink, outline: 'none',
        }}
        onFocus={(e) => { e.target.style.borderColor = P.accent; e.target.style.boxShadow = `0 0 0 3px ${P.accentSoft}`; }}
        onBlur={(e) => { e.target.style.borderColor = P.border; e.target.style.boxShadow = 'none'; }}
      />
    </div>
  );
}

function FilterSelect({ value, onChange, children, testId }) {
  return (
    <select
      data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
      style={{
        padding: '9px 12px', border: `1px solid ${P.border}`, borderRadius: 8,
        background: P.surface, fontSize: 13, color: P.ink, cursor: 'pointer', outline: 'none',
      }}
    >{children}</select>
  );
}

function TextInput(props) {
  return (
    <input
      {...props}
      style={{
        width: '100%', padding: '10px 14px', border: `1px solid ${P.border}`, borderRadius: 8,
        background: P.surface, fontSize: 14, color: P.ink, fontFamily: 'inherit',
        boxSizing: 'border-box', outline: 'none',
      }}
      onFocus={(e) => { e.target.style.borderColor = P.accent; e.target.style.boxShadow = `0 0 0 3px ${P.accentSoft}`; }}
      onBlur={(e) => { e.target.style.borderColor = P.border; e.target.style.boxShadow = 'none'; }}
    />
  );
}

function SField({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 11.5, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700, color: P.slate, marginBottom: 6 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11.5, color: P.muted, marginTop: 5 }}>{hint}</div>}
    </div>
  );
}

function StatChip({ label, value, tone }) {
  const toneMap = {
    success: P.success, warn: P.warn, info: P.info, pink: P.pink,
  };
  return (
    <span>
      <strong style={{ color: toneMap[tone] || P.ink, fontSize: 16, fontVariantNumeric: 'tabular-nums' }}>{(value || 0).toLocaleString()}</strong>
      <span style={{ marginLeft: 6, color: P.muted, fontSize: 12.5 }}>{label}</span>
    </span>
  );
}

function Badge({ tone, children }) {
  const map = {
    success: { bg: P.successSoft, fg: P.success },
    warn:    { bg: P.warnSoft, fg: P.warn },
  };
  const c = map[tone] || map.success;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999,
      background: c.bg, color: c.fg, fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
    }}>{children}</span>
  );
}

function PageBtn({ disabled, onClick, icon: Icon, testId }) {
  return (
    <button
      data-testid={testId} onClick={onClick} disabled={disabled}
      style={{
        width: 34, height: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        border: `1px solid ${P.border}`, background: disabled ? P.borderSoft : P.surface,
        color: disabled ? P.muted : P.slate, borderRadius: 8,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
      }}
    ><Icon size={15} /></button>
  );
}

function LoadingState() { return <div style={{ padding: 60, textAlign: 'center', color: P.muted, fontSize: 13 }}>Loading…</div>; }
function EmptyState({ text }) { return <div style={{ padding: 60, textAlign: 'center', color: P.danger, fontSize: 13 }}>{text}</div>; }

// ─── Admin passkey enrollment / management ───────────────────────────
function AdminPasskeys({ onUnauth }) {
  const [passkeys, setPasskeys] = useState(null);
  const [err, setErr] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [name, setName] = useState('');

  const load = React.useCallback(() => {
    axios.get(`${API}/api/admin/passkey/list`, { headers: authHead() })
      .then((r) => setPasskeys(r.data.passkeys || []))
      .catch((e) => {
        if (e?.response?.status === 401) onUnauth?.();
        else setErr(e?.response?.data?.detail || e.message || 'Failed to load passkeys');
      });
  }, [onUnauth]);

  useEffect(() => { load(); }, [load]);

  const enroll = async () => {
    setErr(''); setEnrolling(true);
    try {
      const token = localStorage.getItem('admin_console_token');
      await enrollAdminPasskey(token, name || `Passkey · ${new Date().toLocaleDateString()}`);
      setName('');
      load();
    } catch (e) {
      if (e?.name === 'NotAllowedError') setErr('Passkey prompt was cancelled.');
      else setErr(e?.response?.data?.detail || e?.message || 'Enrollment failed');
    } finally { setEnrolling(false); }
  };

  const remove = async (pk_id) => {
    if (!window.confirm('Remove this passkey? If it was the only one, 2FA will be disabled until you enrol another.')) return;
    try {
      await axios.delete(`${API}/api/admin/passkey/${pk_id}`, { headers: authHead() });
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const enforced = (passkeys || []).length > 0;

  return (
    <SectionCard
      title="Two-factor authentication (Passkey)"
      subtitle={
        !isPasskeySupported()
          ? 'This browser does not support passkeys. Try Chrome, Safari, Edge or Firefox on a device with Touch ID / Windows Hello / a security key.'
          : enforced
          ? 'Passkey 2FA is ACTIVE. Every admin login will require a passkey after your password.'
          : 'Add a passkey to require Touch ID / Windows Hello / a security key on every future admin login.'
      }>
      <div data-testid="admin-passkeys">
        {isPasskeySupported() && (
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <input
              data-testid="passkey-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name this device (e.g. MacBook Pro)"
              style={{
                flex: 1, minWidth: 220, padding: '10px 14px', border: `1px solid ${P.border}`,
                borderRadius: 8, background: P.surface, fontSize: 14, color: P.ink, outline: 'none',
              }}
            />
            <button
              data-testid="passkey-enroll-btn"
              onClick={enroll}
              disabled={enrolling}
              style={{
                padding: '10px 16px', background: P.primary, color: '#fff',
                border: 'none', borderRadius: 8, fontSize: 13.5, fontWeight: 600,
                cursor: enrolling ? 'wait' : 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 8,
              }}>
              <Fingerprint size={14} />
              {enrolling ? 'Waiting for device…' : 'Enrol new passkey'}
            </button>
          </div>
        )}

        {err && (
          <div data-testid="passkey-error" style={{
            padding: '10px 14px', background: P.dangerSoft, color: P.danger,
            border: `1px solid ${P.danger}33`, borderRadius: 8, fontSize: 13, marginBottom: 14,
          }}>{err}</div>
        )}

        {passkeys === null ? (
          <div style={{ color: P.muted, fontSize: 13 }}>Loading…</div>
        ) : passkeys.length === 0 ? (
          <div style={{
            padding: '18px 20px', background: P.warnSoft, border: `1px solid ${P.warn}33`,
            borderRadius: 8, fontSize: 13.5, color: P.warn, fontWeight: 500,
            display: 'flex', gap: 10, alignItems: 'flex-start',
          }} data-testid="passkey-none">
            <ShieldCheck size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>
              No passkeys enrolled yet. Your admin login is currently protected by password only.
              Enrolling at least one passkey activates mandatory 2FA on every future sign-in.
            </span>
          </div>
        ) : (
          <div style={{ border: `1px solid ${P.border}`, borderRadius: 8, overflow: 'hidden' }}>
            {passkeys.map((pk, i) => (
              <div key={pk.id} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '12px 16px',
                borderTop: i === 0 ? 'none' : `1px solid ${P.borderSoft}`,
              }}>
                <Fingerprint size={20} color={P.primary} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: P.ink }}>{pk.name}</div>
                  <div style={{ fontSize: 12, color: P.muted, marginTop: 2 }}>
                    Enrolled {new Date(pk.created_at).toLocaleDateString()}
                    {pk.last_used_at && ` · Last used ${new Date(pk.last_used_at).toLocaleDateString()}`}
                  </div>
                </div>
                <button
                  data-testid={`passkey-remove-${pk.id}`}
                  onClick={() => remove(pk.id)}
                  aria-label="Remove passkey"
                  style={{
                    background: 'transparent', border: `1px solid ${P.border}`,
                    padding: '6px 10px', borderRadius: 6, color: P.danger, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5,
                  }}>
                  <Trash2 size={13} /> Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionCard>
  );
}


// Extract the marketing-SMTP-related fields from the settings payload
// into a self-contained sub-state. Password is never round-tripped through
// the UI — we only send a new one when the admin types into that field.
function smtpFromSettings(s) {
  return {
    marketing_smtp_enabled: !!s.marketing_smtp_enabled,
    marketing_smtp_host: s.marketing_smtp_host || '',
    marketing_smtp_port: s.marketing_smtp_port || 587,
    marketing_smtp_username: s.marketing_smtp_username || '',
    marketing_smtp_password: '',
    marketing_smtp_password_set: !!s.marketing_smtp_password_set,
    marketing_smtp_encryption: s.marketing_smtp_encryption || 'tls',
    marketing_smtp_from_email: s.marketing_smtp_from_email || '',
    marketing_smtp_from_name: s.marketing_smtp_from_name || 'Keystone by CloudVoro',
    marketing_smtp_inquiry_to_email: s.marketing_smtp_inquiry_to_email || '',
  };
}

const tableTh = { textAlign: 'left', padding: '10px 14px', fontWeight: 700, fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase', whiteSpace: 'nowrap' };
const tableTd = { padding: '10px 14px', color: P.slate, verticalAlign: 'top' };
